import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import { Button, Icon, Text } from '@chakra-ui/react'
import React, { useContext, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { RiDeleteBinLine } from 'react-icons/ri'
import { useHistory } from 'react-router-dom'
import useFetch, { CachePolicies } from 'use-http'
import useToast from '../../hooks/useToast'
import type { DeleteMediaCollectionErrorResponse } from '../../interfaces'
import { MLContext } from '../../providers'
import { MediaLibraryActions } from '../../providers/MLProvider'

interface Props {
  isOpen: boolean
  onClose(): void
}

const DeleteMediaCollectionModal = ({
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const [
    {
      selectedMediaCollections,
      selectedBreadcrumbMediaCollection,
      refetchMediaCollections,
    },
    dispatch,
  ] = useContext(MLContext)

  const { response, request, loading } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const history = useHistory()

  const deleteMediaCollections = async (): Promise<void> => {
    setIsSubmitting(true)
    if (selectedMediaCollections.length > 0) {
      let isBreadcrumbCollectionDeleted = false
      const deletedCollectionsStatus: number[] = []

      for await (const mediaCollection of selectedMediaCollections) {
        const res = (await request.delete(
          `/api/v2/media-collections/${mediaCollection.mediaCollectionId}`,
        )) as null | DeleteMediaCollectionErrorResponse

        if (request.error) {
          const collectionName = mediaCollection.name
          toast({
            description: res?.message ?? (
              <Trans t={t} i18nKey="error_in_deleting_media_collection">
                Error in deleting media collection {{ collectionName }}, please
                try again after sometime.
              </Trans>
            ),
            status: 'error',
            isClosable: true,
            duration: 5000,
            position: 'top-right',
            variant: 'outline',
          })
        } else {
          if (
            selectedBreadcrumbMediaCollection?.mediaCollectionId ===
            mediaCollection.mediaCollectionId
          ) {
            isBreadcrumbCollectionDeleted = true
          }
          deletedCollectionsStatus.push(response.status)
        }
      }
      if (deletedCollectionsStatus.length > 0) {
        const count = deletedCollectionsStatus.length
        toast({
          description: (
            <Trans
              t={t}
              i18nKey="media_collections_deleted_successfully"
              count={count}
            >
              {{ count }} media collection(s) deleted successfully
            </Trans>
          ),
          status: 'success',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })

        window.trackEvent({
          action: 'Submit',
          category: 'Media collection',
          label: 'Delete',
          value: count,
        })
      }

      if (isBreadcrumbCollectionDeleted) {
        history.replace('/media-collections')
      }
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Icon
            as={RiDeleteBinLine}
            color="appBattleshipGrey"
            backgroundColor="appPaleGrey"
            boxSize={6}
            padding="0.1em"
            borderRadius="full"
            marginRight="0.5em"
          />
          {t('delete_media_collections', 'Delete media collections')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text color="appBattleshipGrey">
            {t(
              'delete_media_collection_description',
              'This will permanently delete all the selected media collections. This operation also disassociates all the assets, users, and user groups associated with the media collection. Once submitted, this action can not be reversed.',
            )}
          </Text>
        </ModalBody>
        <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
          <Button onClick={onClose} variant="outline">
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            colorScheme="appRed"
            isDisabled={loading}
            isLoading={loading || isSubmitting}
            onClick={async () => {
              await deleteMediaCollections()
              await new Promise(resolve => setTimeout(resolve, 1500))
              setIsSubmitting(false)
              await refetchMediaCollections()
              dispatch({
                type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
              })
              onClose()
            }}
          >
            {t('submit', 'Submit')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default DeleteMediaCollectionModal
